import React, { useEffect, useMemo, useRef, useState } from 'react';

import type { AttributeDropdownProps } from '../../data-layer/types';

import './AttributeDropdown.css';

// Debounce hook
function useDebounce(value: string, delay: number) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

/**
 * AttributeDropdown component for selecting Saviynt fields
 */
function AttributeDropdown({
  options,
  value,
  onChange,
  placeholder = 'Select field',
  disabled = false,
  error,
}: AttributeDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const debouncedSearchText = useDebounce(searchText, 300);

  // Filter options based on search text
  const filteredOptions = useMemo(() => {
    if (debouncedSearchText.length === 0 || debouncedSearchText.length < 3) {
      return options.slice(0, 25); // Show first 25 by default
    }

    return options
      .filter((option) =>
        option.toLowerCase().includes(debouncedSearchText.toLowerCase())
      )
      .sort((a, b) => {
        const searchTerm = debouncedSearchText.toLowerCase();

        const getAttributeName = (str: string) => {
          const parts = str.split('.');

          return parts.length > 1 ? parts[1] : str;
        };

        const attrA = getAttributeName(a).toLowerCase();
        const attrB = getAttributeName(b).toLowerCase();

        if (attrA === searchTerm && attrB !== searchTerm) {
          return -1;
        }

        if (attrB === searchTerm && attrA !== searchTerm) {
          return 1;
        }

        if (attrA.startsWith(searchTerm) && !attrB.startsWith(searchTerm)) {
          return -1;
        }

        if (attrB.startsWith(searchTerm) && !attrA.startsWith(searchTerm)) {
          return 1;
        }

        return attrA.length - attrB.length;
      })
      .slice(0, 50);
  }, [options, debouncedSearchText]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setSearchText('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
        setSearchText('');
      }
    };

    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  // Auto-focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const handleOptionSelect = (option: string) => {
    onChange(option);
    setIsOpen(false);
    setSearchText('');
  };

  const handleToggle = () => {
    if (!disabled) {
      setIsOpen(!isOpen);

      if (!isOpen) {
        setSearchText('');
      }
    }
  };

  return (
    <div className='attribute-dropdown' ref={dropdownRef}>
      <button
        type='button'
        className={`attribute-dropdown-trigger ${error ? 'error' : ''} ${
          disabled ? 'disabled' : ''
        }`}
        onClick={handleToggle}
        disabled={disabled}
        aria-expanded={isOpen}
        aria-haspopup='listbox'>
        <span className='attribute-dropdown-value'>{value || placeholder}</span>
        <svg
          className={`attribute-dropdown-arrow ${isOpen ? 'open' : ''}`}
          width='12'
          height='8'
          viewBox='0 0 12 8'
          fill='none'>
          <path
            d='M1 1.5L6 6.5L11 1.5'
            stroke='currentColor'
            strokeWidth='1.5'
            strokeLinecap='round'
            strokeLinejoin='round'
          />
        </svg>
      </button>

      {isOpen && (
        <div className='attribute-dropdown-menu'>
          <div className='attribute-dropdown-search'>
            <input
              ref={searchInputRef}
              type='text'
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder='Type 3+ chars to search...'
              className='attribute-dropdown-search-input'
            />
          </div>

          <div className='attribute-dropdown-options'>
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => (
                <button
                  key={option}
                  type='button'
                  className={`attribute-dropdown-option ${
                    value === option ? 'selected' : ''
                  }`}
                  onClick={() => handleOptionSelect(option)}>
                  {option}
                </button>
              ))
            ) : (
              <div className='attribute-dropdown-no-options'>
                No fields available
              </div>
            )}
          </div>
        </div>
      )}

      {error && <div className='attribute-dropdown-error'>{error}</div>}
    </div>
  );
}

export default AttributeDropdown;
